import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { motion } from 'framer-motion';
import {
  Users,
  Heart,
  ChevronRight,
  MessageCircle
} from 'lucide-react';

export default function DashboardMatchingTab({
  user,
  stats,
  conversations,
  soloTravelers,
  handleOpenConversation,
  handleOpenMessage,
  handleMatchRequest
}) {
  return (
    <div className="space-y-6">
      {/* Inbox Section */}
      {conversations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-secondary" />
              Your Messages
              {stats.unread_messages > 0 && (
                <Badge className="bg-red-500 ml-2">{stats.unread_messages} new</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {conversations.map(conv => (
                <div
                  key={conv.partner_id}
                  onClick={() => handleOpenConversation(conv)}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-secondary/20 text-secondary">
                        {conv.partner_name?.split(' ').map(n => n[0]).join('') || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-primary">{conv.partner_name}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {conv.last_message}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.unread_count > 0 && (
                      <Badge className="bg-secondary">{conv.unread_count}</Badge>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-secondary" />
            Solo Travelers Looking for Connections
          </CardTitle>
          <CardDescription>
            Connect with other travelers interested in the same trips. Find a roommate or travel buddy!
          </CardDescription>
        </CardHeader>
        <CardContent>
          {soloTravelers.length === 0 ? (
            /* Empty State - No travelers yet */
            <div className="text-center py-10">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="font-semibold text-lg text-primary mb-2">
                Looking for a Travel Partner?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Be the first to express interest! When others sign up for the same trips, we'll help you connect.
              </p>
              <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-sm text-muted-foreground mb-3">
                  Fill out your travel interest above to let others know you're looking for connections.
                </p>
                <Button 
                  variant="outline" 
                  className="border-secondary text-secondary hover:bg-secondary hover:text-white"
                  onClick={() => {
                    document.getElementById('trip-interest-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <Heart className="h-4 w-4 mr-2" />
                  Express Interest
                </Button>
              </div>
            </div>
          ) : (
            /* Real Travelers List */
            <>
              <div className="space-y-4">
                {soloTravelers.map(traveler => (
                  <motion.div
                    key={traveler.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-secondary/20 text-secondary">
                          {traveler.name?.split(' ').map(n => n[0]).join('') || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-primary">{traveler.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {traveler.tripInterest} • {traveler.dates}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{traveler.lookingFor}</Badge>
                          {traveler.bio && (
                            <span className="text-xs text-muted-foreground">"{traveler.bio}"</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleOpenMessage(traveler)}
                        data-testid={`message-btn-${traveler.id}`}
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        Message
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default"
                        className="bg-secondary hover:bg-secondary/90"
                        onClick={() => handleMatchRequest(traveler.id, 'request')}
                      >
                        <Heart className="h-4 w-4 mr-1" />
                        Connect
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>How it works:</strong> When you connect with another traveler and they accept, 
                  we'll email you both to coordinate. You can also message travelers directly!
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
